import React, { useEffect, useState } from "react";
import { AlertTriangle, Sparkles, CreditCard, ArrowUpRight } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { onDeviceCountChanged } from "@/lib/deviceCountEvents";

/**
 * PlanLimitBanner — sticky banner above the page content that shows
 * tenant plan usage + an upgrade nudge when ≥80% capacity is hit.
 *
 * Hidden when:
 *  - we're still loading the snapshot
 *  - tenant is on a plan with no caps (enterprise / unmetered)
 *  - usage is < 60% across all metered axes (don't be noisy)
 *
 * Polls every 60s. Single round-trip to /api/billing/subscription.
 */
export default function PlanLimitBanner() {
    const { user } = useAuth();
    const [sub, setSub] = useState(null);
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const { data } = await http.get("/billing/subscription");
            setSub(data);
        } catch { /* fail silently — banner is non-critical */ }
    };

    useEffect(() => {
        if (!user) return;
        refresh();
        const iv = setInterval(refresh, 60000);
        // Immediate refresh when the Devices page adds/removes an agent --
        // don't make the user wait up to 60s to see "1/10" become "2/10"
        // right after they connect storage or enroll a device.
        const unsub = onDeviceCountChanged(refresh);
        return () => { clearInterval(iv); unsub(); };
    }, [user]);

    if (!sub || !user) return null;
    const dev = sub.usage?.devices || {};
    const con = sub.usage?.concurrent || {};
    const status = sub.status || "active";
    const capped = (dev.cap || con.cap);
    const maxPct = Math.max(dev.pct || 0, con.pct || 0);

    const pastDue = status === "past_due";
    const canceled = status === "canceled";
    const showUsage = capped && maxPct >= 60;

    if (!pastDue && !canceled && !showUsage) return null;

    const goToPortal = async () => {
        setBusy(true);
        try {
            const r = await http.post("/billing/portal", { return_url: window.location.href });
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Stripe portal not available");
        } finally { setBusy(false); }
    };

    if (pastDue) {
        return (
            <Banner tone="red" testid="plan-banner-past-due">
                <AlertTriangle size={14} />
                <span>
                    <b>Payment failed.</b> Your subscription will be downgraded if not resolved.
                </span>
                <PortalBtn onClick={goToPortal} busy={busy} label="Update card" />
            </Banner>
        );
    }
    if (canceled) {
        return (
            <Banner tone="amber" testid="plan-banner-canceled">
                <AlertTriangle size={14} />
                <span>
                    Your subscription is <b>canceled</b>. You're now on the <b>Starter</b> plan.
                </span>
                <PortalBtn onClick={goToPortal} busy={busy} label="Resubscribe" />
            </Banner>
        );
    }
    // Capacity warning (≥60%)
    const critical = maxPct >= 90;
    const tone = critical ? "red" : maxPct >= 80 ? "amber" : "blue";
    return (
        <Banner tone={tone} testid="plan-banner-usage">
            <Sparkles size={14} />
            <span>
                <b>{sub.plan_label}</b> plan ·{" "}
                {/* "fleet devices", not bare "devices" -- db.agents (what
                    this counts) includes storage connection records and is
                    a different metric from the Client Portal's "license
                    seats" (license_activations), which happens to share
                    the same plan cap number and would otherwise read as
                    the same count. */}
                {dev.cap != null && (
                    <span data-testid="plan-banner-devices">
                        {dev.used}/{dev.cap} fleet devices ({dev.pct}%)
                    </span>
                )}
                {dev.cap != null && con.cap != null && " · "}
                {con.cap != null && (
                    <span data-testid="plan-banner-concurrent">
                        {con.used}/{con.cap} concurrent jobs ({con.pct}%)
                    </span>
                )}
                {critical && " · cap will block new work soon."}
            </span>
            <a
                href="/billing/upgrade"
                onClick={(e) => { e.preventDefault(); goToPortal(); }}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-white/25"
                data-testid="plan-banner-upgrade"
            >
                <CreditCard size={11} /> Manage plan <ArrowUpRight size={11} />
            </a>
        </Banner>
    );
}

function Banner({ tone, testid, children }) {
    const toneCls = {
        red:   "border-[hsl(var(--destructive))]/50 bg-[hsl(var(--destructive))]/[0.08] text-[hsl(var(--destructive))]",
        amber: "border-amber-500/50 bg-amber-500/[0.08] text-amber-300",
        blue:  "border-[#5b5ef0]/50 bg-[#5b5ef0]/[0.08] text-[#a5a8ff]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className={`flex flex-wrap items-center gap-2 border-b border-l-4 px-4 py-2 text-xs ${toneCls}`}
        >
            {children}
        </div>
    );
}

function PortalBtn({ onClick, busy, label }) {
    return (
        <button
            onClick={onClick}
            disabled={busy}
            data-testid="plan-banner-portal-btn"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-background hover:opacity-90 disabled:opacity-50"
        >
            <CreditCard size={11} /> {busy ? "Loading…" : label}
        </button>
    );
}
